import { Icon, notificationsManager, useModal } from '@innowise-group/mui-kit';
import React, { useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as Styled from './dropzone.styles';
import { FileIdResponse, useCandidateFilesAPI } from '@innowise-group/core';
import { FilesAcceptFormats, FilesFormats, FileTypeConstants, MaxFilesCount, MaxFileSize } from '@constants';
import { FileAccessModal, FilePreviewModal } from '@innowise-group/modals';
import { FileItem } from '@shared-components';

interface DropzoneProps {
  fieldName: string;
  fileType?: FileTypeConstants;
  disabled?: boolean;
}

const Dropzone: React.FC<DropzoneProps> = ({ fieldName, fileType, disabled }) => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showFileAccessModal, closeFileAccessModal] = useModal(FileAccessModal);
  const [showFilePreview] = useModal(FilePreviewModal);
  const { createFileId } = useCandidateFilesAPI();

  const { watch, setValue } = useFormContext();

  const files = watch(fieldName) || [];

  const fileInputColumnStart = useMemo(() => {
    const attachmentsCount = files.length;
    if (attachmentsCount > 10) return 1;
    if (attachmentsCount > 5) return 3;
    if (attachmentsCount <= 5 && attachmentsCount) return 2;
    if (!attachmentsCount) return 0;
    return 1;
  }, [files]);

  const inputStatus = useMemo(() => {
    if (files.length < MaxFilesCount) return 'default';
    if (files.length >= MaxFilesCount) return 'limit';
  }, [files]);

  const isFilesInputDisabled = useMemo(() => disabled || files.length >= MaxFilesCount, [disabled, files]);

  const handleOpenFile = (file: FileIdResponse) => () => {
    showFilePreview({
      fileName: file.filename,
      id: file.id,
    });
  };

  const closeModal = () => {
    closeFileAccessModal();
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleAddStrictAccessFile = (event: React.ChangeEvent<HTMLInputElement>) => async () => {
    try {
      const filesResponse = await createFileId({ file: event.target.files[0], isProtected: true, fileType });
      setValue(fieldName, [...files, filesResponse]);
      closeModal();
      fileInputRef.current.value = null;
    } catch {
      closeModal();
    }
  };

  const handleAddFile = (event: React.ChangeEvent<HTMLInputElement>) => async () => {
    try {
      const filesResponse = await createFileId({ file: event.target.files[0], isProtected: false, fileType });
      setValue(fieldName, [...files, filesResponse]);
      closeModal();
      fileInputRef.current.value = null;
    } catch {
      closeModal();
    }
  };

  const openFileAccessModal = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files[0]) {
      const fileType = event.target.files[0].type || event.target.files[0].name.split('.').reverse()[0];
      if (event.target.files[0] && event.target.files[0].size <= MaxFileSize && FilesFormats.includes(fileType)) {
        showFileAccessModal({ successCallback: handleAddStrictAccessFile(event), closeCallback: handleAddFile(event) });
      } else {
        notificationsManager.error({
          subtitle: t(
            `notifications.text.${
              !(event.target.files[0]?.size <= MaxFileSize) ? 'invalidFileSize' : 'invalidFileFormat'
            }`,
          ),
        });
        fileInputRef.current.value = null;
      }
    }
  };

  const handleDragEnter = () => {
    wrapperRef.current.classList.add('dragover');
  };
  const handleDragLeave = () => {
    wrapperRef.current.classList.remove('dragover');
  };

  const handleDeleteFile = (id: number) => (event: React.MouseEvent<HTMLOrSVGElement>) => {
    event.stopPropagation();
    setValue(
      fieldName,
      files.filter((item) => item.id !== id),
    );
  };

  return (
    <Styled.FilesContainer column={fileInputColumnStart}>
      {!!files.length &&
        files.map((item) => (
          <FileItem
            key={item.id}
            file={item}
            disabled={disabled}
            onClick={handleOpenFile(item)}
            onDelete={!disabled ? handleDeleteFile(item.id) : undefined}
            showDelete
          />
        ))}
      <Styled.InputContainer
        ref={wrapperRef}
        onDragLeave={handleDragLeave}
        onDragEnter={handleDragEnter}
        onDrop={handleDragLeave}
        status={inputStatus}
        columnStart={fileInputColumnStart}
        disabled={isFilesInputDisabled}
      >
        {files.length < MaxFilesCount ? (
          <React.Fragment>
            <Styled.FileInput
              type="file"
              onChange={openFileAccessModal}
              ref={fileInputRef}
              accept={FilesAcceptFormats.join(', ')}
              disabled={isFilesInputDisabled}
            />
            <Styled.FileInputPlaceholder>
              <Icon type="u_download_files" size={100} viewBox="0 0 100 100" />
              <Styled.FileInputBlockDefault>
                <Styled.DefaultTitle>{t('pages.candidates.candidateDetails.downloadTheFiles')}</Styled.DefaultTitle>
                <span>
                  <Trans
                    i18nKey="pages.candidates.candidateDetails.filePlaceholder"
                    components={{
                      highlight: <Styled.DefaultHightLighted />,
                    }}
                  />
                </span>
              </Styled.FileInputBlockDefault>
            </Styled.FileInputPlaceholder>
          </React.Fragment>
        ) : (
          <Styled.FileInputPlaceholder>
            <Icon type="u_files_count_limit" size={100} viewBox="0 0 100 100" />
            <Styled.FileInputBlockLimit>
              <Styled.LimitTitle>{t('pages.candidates.candidateDetails.downloadingFiles')}</Styled.LimitTitle>
              <Styled.LimitDescriptionBlock>
                <span>{t('pages.candidates.candidateDetails.limitFilesDescription')}</span>
                <Styled.LimitDescriptionText>
                  {t('pages.candidates.candidateDetails.limitFilesText')}
                </Styled.LimitDescriptionText>
              </Styled.LimitDescriptionBlock>
            </Styled.FileInputBlockLimit>
          </Styled.FileInputPlaceholder>
        )}
      </Styled.InputContainer>
    </Styled.FilesContainer>
  );
};

export default React.memo(Dropzone);
